/**
 * SimulatorStudioCard — carte d'un simulateur du « Studio d'entraînement ».
 *
 * UNE carte pour l'accueil (`DashboardPage`) et la page « Entraînement »
 * (`MEntrainerPage`) : l'accueil avait sa propre version, centrée et sans
 * hauteur imposée — chaque carte prenait la taille de son texte, aucune ne
 * s'alignait sur sa voisine (demande de Valérie du 21/10/2026). La carte
 * remplit sa cellule de grille (`h-full w-full`) : toutes les cartes d'une
 * ligne ont la même hauteur.
 *
 * La grille (`STUDIO_GRID_CLASS`) est partagée pour la même raison.
 */
import { Play, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AIAgent } from "../types";
import { pickSimulatorDescription } from "../lib/simulator-description-i18n";

export const STUDIO_GRID_CLASS = "grid gap-3 sm:grid-cols-2 lg:grid-cols-3";

interface SimulatorStudioCardProps {
  agent: AIAgent;
  /** Simulateur éteint (pas de licence, ou désactivé) : grisé et non cliquable. */
  disabled: boolean;
  /** Simulateur recommandé : liseré violet et badge « Recommandé ». */
  isReco?: boolean;
  onStart: (agent: AIAgent) => void;
  /** Ancre des visites guidées (`data-tour`). */
  dataTour?: string;
}

export function SimulatorStudioCard({ agent, disabled, isReco = false, onStart, dataTour }: SimulatorStudioCardProps) {
  const { t, i18n } = useTranslation();
  const accent = agent.color || "#988EF2";
  const emoji = agent.icon && !agent.icon.startsWith("data:") && !agent.icon.startsWith("http") ? agent.icon : "🎯";
  return (
    <button
      data-tour={dataTour}
      disabled={disabled}
      onClick={() => !disabled && onStart(agent)}
      className={`relative flex h-full w-full flex-col gap-2 rounded-2xl border bg-white p-3.5 text-left shadow-[var(--elev-1)] transition ${disabled ? "opacity-50" : "cursor-pointer hover:-translate-y-0.5 hover:shadow-[var(--elev-2)]"}`}
      style={isReco ? { borderTopWidth: 3, borderTopColor: "#5A4BBE" } : undefined}
    >
      {isReco && (
        <span className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-white" style={{ background: "#5A4BBE" }}><Star className="h-2.5 w-2.5" fill="white" />{t("mEntrainer.recommended")}</span>
      )}
      <div className="flex items-center gap-3">
        {agent.image ? (
          <img src={agent.image} alt="" loading="lazy" className="h-12 w-12 shrink-0 rounded-full object-cover" style={{ objectPosition: "top", boxShadow: `0 0 0 2.5px #fff, 0 0 0 4.5px ${accent}` }} />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-xl text-white" style={{ background: accent, boxShadow: `0 0 0 2.5px #fff, 0 0 0 4.5px ${accent}` }}>{emoji}</div>
        )}
        <div className="min-w-0 flex-1 font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{agent.name}</div>
      </div>
      <p className="line-clamp-2 min-h-[32px] text-xs text-muted-foreground">{pickSimulatorDescription(agent, i18n.language)}</p>
      {/* `mt-auto` : le bouton se cale en bas, à la même hauteur sur toute la ligne. */}
      {!disabled && (
        <span className="mt-auto inline-flex items-center gap-1.5 self-start rounded-lg px-3 py-1.5 text-xs font-bold text-white" style={{ background: accent }}>
          <Play className="h-3.5 w-3.5" fill="white" />{t("mEntrainer.trainTitle")}
        </span>
      )}
    </button>
  );
}
